import * as Sentry from '@sentry/react'
import { loadStripe } from '@stripe/stripe-js'
import type { Stripe } from '@stripe/stripe-js'
import { getDirectApi } from 'mytours-read-common'
import { captureApiException, isNetworkError } from '../api/errors'

// How stripe-js words a script that never ran: `onerror` fired, or it left no `window.Stripe`.
export const STRIPE_SCRIPT_UNAVAILABLE = ['Failed to load Stripe.js', 'Stripe.js not available']

/** The Stripe instance, or `null` when it cannot be had; `<Elements>` then renders no payment UI. */
export async function loadStripeForProject(): Promise<Stripe | null> {
	try {
		const configs = await getDirectApi().paymentConfig()
		// Unguarded, stripe-js words the same outage as its own TypeError; name the deploy instead.
		const key = configs?.publishable_key
		if (typeof key !== 'string' || key === '') {
			Sentry.captureException(new Error('paymentConfig answered with no usable publishable key'))
			return null
		}
		// `await`, not a bare `return`: an async function adopts a returned promise past its own catch.
		return await loadStripe(key)
	} catch (error) {
		const projectDeactivated = error instanceof Response && error.status === 401
		// Offline a blocked script is weather; online it took payments down. No flag is not offline.
		const stripeUnreachable =
			error instanceof Error && STRIPE_SCRIPT_UNAVAILABLE.includes(error.message) && navigator.onLine === false
		if (!projectDeactivated && !isNetworkError(error) && !stripeUnreachable) {
			captureApiException(error, 'paymentConfig')
		}
		return null
	}
}
